import React, { useEffect, useMemo, useState } from "react";
import {
  Thermometer,
  Cpu,
  Building2,
  Refrigerator,
  Activity,
  AlertTriangle,
  Edit,
  Search,
  Radio,
} from "lucide-react";

function StatChip({ label, value, icon: Icon, subtle }) {
  return (
    <div
      className={
        "flex items-center gap-2 rounded-xl border px-3 py-2 text-sm " +
        (subtle
          ? "border-slate-200 bg-white text-slate-700"
          : "border-slate-900 bg-slate-900 text-white")
      }
    >
      <div
        className={
          "flex h-7 w-7 items-center justify-center rounded-lg " +
          (subtle ? "bg-slate-100" : "bg-white/10")
        }
      >
        <Icon size={15} className={subtle ? "text-slate-500" : "text-white"} />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide opacity-80">{label}</div>
        <div className="text-sm font-semibold">{value}</div>
      </div>
    </div>
  );
}

function Badge({ children, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-slate-100 text-slate-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    sky: "border-sky-200 bg-sky-50 text-sky-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function SearchField({ value, onChange, placeholder }) {
  return (
    <label className="relative block">
      <Search
        size={14}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 outline-none focus:border-slate-400"
      />
    </label>
  );
}

const norm = (value) => String(value || "").toLowerCase().trim();
const isAssignedToFrigo = (item) => Number(item?.frigo_id || item?.frigoId || 0) > 0;
const isAssignedToCompany = (item) => Number(item?.azienda_id || 0) > 0;

function getDeviceKind(item) {
  if (item.device_kind) return item.device_kind;
  if (item.source === "lorawan") return "lorawan";
  if (item.source === "cryo") return "cryo_real";
  return "cryo_manual";
}

function getKindLabel(kind) {
  if (kind === "cryo_core") return "Cryo abbattimenti";
  if (kind === "cryo_real") return "Cryo reale";
  if (kind === "cryo_manual") return "Manuale";
  if (kind === "lorawan") return "LoRaWAN";
  return "Dispositivo";
}

function kindTone(kind) {
  if (kind === "cryo_core") return "emerald";
  if (kind === "cryo_real") return "sky";
  if (kind === "cryo_manual") return "amber";
  if (kind === "lorawan") return "violet";
  return "slate";
}

function normalizeCryoItems(items) {
  return (Array.isArray(items) ? items : []).map((item) => ({
    ...item,
    mac_address: item.mac_address || item.mac || "",
    device_kind: item.device_model === "cryo_core" ? "cryo_core" : (item.source === "cryo" ? "cryo_real" : "cryo_manual"),
  }));
}

function normalizeLorawanItems(items) {
  return (Array.isArray(items) ? items : []).map((item) => ({
    ...item,
    mac_address: item.mac_address || item.mac || "",
    source: "lorawan",
    device_kind: "lorawan",
  }));
}

function ToggleGroup({ value, onChange, options }) {
  return (
    <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 p-1 text-xs shadow-sm">
      {options.map(([optionValue, label]) => (
        <button
          key={optionValue}
          className={
            "rounded-full px-3 py-1 " +
            (value === optionValue
              ? "bg-slate-900 text-white"
              : "text-slate-600 hover:bg-slate-100")
          }
          onClick={() => onChange(optionValue)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function DispositiviCryo({ isSuper }) {
  const [dispositivi, setDispositivi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState("");
  const [apiIsSuper, setApiIsSuper] = useState(false);
  const [aziendaId, setAziendaId] = useState(null);
  const [frigoFilter, setFrigoFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [macQuery, setMacQuery] = useState("");
  const [coreFlagByDevice, setCoreFlagByDevice] = useState({});
  const [assignCompanyByDevice, setAssignCompanyByDevice] = useState({});
  const [assigningDevice, setAssigningDevice] = useState(null);

  const loadData = async () => {
    setLoading(true);
    setErrore("");
    try {
      const [cryoRes, lorawanRes] = await Promise.all([
        fetch("/api/admin/sonde_list.php", { credentials: "include" }),
        fetch("/api/admin/lorawan_devices_list.php?limit=500", {
          credentials: "include",
        }),
      ]);

      const cryoData = await cryoRes.json();
      const lorawanData = await lorawanRes.json();

      if (!cryoData.ok) {
        throw new Error(cryoData.error || "Risposta non valida da sonde_list");
      }
      if (!lorawanData.ok) {
        throw new Error(
          lorawanData.error || "Risposta non valida da lorawan_devices_list"
        );
      }

      setApiIsSuper(!!cryoData.isSuper);
      setAziendaId(cryoData.aziendaId || null);
      const merged = [
        ...normalizeCryoItems(cryoData.items),
        ...normalizeLorawanItems(lorawanData.items),
      ];
      setDispositivi(merged);
      setCoreFlagByDevice((prev) => {
        const next = { ...prev };
        merged.forEach((item) => {
          const id = item.device_id || item.id;
          if (id && next[id] === undefined) next[id] = getDeviceKind(item) === "cryo_core";
        });
        return next;
      });
      setAssignCompanyByDevice((prev) => {
        const next = { ...prev };
        merged.forEach((item) => {
          const id = item.device_id || item.id;
          if (id && next[id] === undefined && item.azienda_id) next[id] = String(item.azienda_id);
        });
        return next;
      });
    } catch (err) {
      console.error(err);
      setErrore("Errore caricamento dispositivi: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const effectiveIsSuper = !!(isSuper || apiIsSuper);

  const filteredDispositivi = useMemo(() => {
    return dispositivi.filter((item) => {
      const kind = getDeviceKind(item);

      if (typeFilter !== "all" && kind !== typeFilter) return false;
      if (frigoFilter === "assigned" && !isAssignedToFrigo(item)) return false;
      if (frigoFilter === "free" && isAssignedToFrigo(item)) return false;
      if (companyFilter === "company_assigned" && !isAssignedToCompany(item)) {
        return false;
      }
      if (companyFilter === "company_free" && isAssignedToCompany(item)) {
        return false;
      }
      if (
        macQuery &&
        !norm(item.mac_address).includes(norm(macQuery)) &&
        !norm(item.mac).includes(norm(macQuery))
      ) {
        return false;
      }

      return true;
    });
  }, [dispositivi, typeFilter, frigoFilter, companyFilter, macQuery]);

  const totals = useMemo(() => {
    const cryoReal = dispositivi.filter((d) => getDeviceKind(d) === "cryo_real").length;
    const cryoCore = dispositivi.filter((d) => getDeviceKind(d) === "cryo_core").length;
    const cryoManual = dispositivi.filter((d) => getDeviceKind(d) === "cryo_manual").length;
    const lorawan = dispositivi.filter((d) => getDeviceKind(d) === "lorawan").length;
    const companyAssigned = dispositivi.filter((d) => isAssignedToCompany(d)).length;

    return {
      total: dispositivi.length,
      cryoReal,
      cryoCore,
      cryoManual,
      lorawan,
      companyAssigned,
      frigoAssigned: dispositivi.filter((d) => isAssignedToFrigo(d)).length,
    };
  }, [dispositivi]);

  const rinominaSonda = async (item) => {
    if (getDeviceKind(item) === "lorawan") {
      alert("La rinomina da questa pagina e disponibile solo per i Cryo.");
      return;
    }

    const mac = item.mac_address || item.mac;
    if (!mac) {
      alert("MAC non disponibile per questo dispositivo.");
      return;
    }

    const attuale = item.nome || item.label || item.nome_sonda || item.alias || mac;
    const nuovo = window.prompt("Nuovo nome sonda", attuale);
    if (!nuovo || nuovo === attuale) return;

    try {
      const res = await fetch("/api/admin/sonda_rename.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mac, nome: nuovo }),
      });
      const text = await res.text();
      let data;

      try {
        data = JSON.parse(text);
      } catch {
        alert("Risposta non valida da sonda_rename.php:\n" + text);
        return;
      }

      if (!data.ok) {
        alert("Errore rinomina sonda: " + (data.error || "ERRORE"));
        return;
      }

      loadData();
    } catch (err) {
      alert("Errore rinomina sonda: " + err.message);
    }
  };

  const salvaCryoAbbattimenti = async (item) => {
    const deviceId = item.device_id || item.id;
    const isCore = !!coreFlagByDevice[deviceId];
    const aziendaScelta = Number(assignCompanyByDevice[deviceId] || "");

    if (!deviceId) {
      alert("Dispositivo non valido.");
      return;
    }

    if (isCore && !aziendaScelta) {
      alert("Inserisci l'ID azienda prima di salvare il Cryo a cuore.");
      return;
    }

    const nome = item.nome || item.label || item.nome_sonda || `Cryo abbattimenti ${item.mac_address || item.mac}`;

    try {
      setAssigningDevice(deviceId);
      const res = await fetch("/api/admin/cryo_abbattimenti_assign.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_id: deviceId,
          is_cryo_core: isCore,
          azienda_id: aziendaScelta,
          nome,
        }),
      });
      const data = await res.json();
      if (!data.ok) {
        throw new Error(data.message || data.error || "Errore assegnazione Cryo abbattimenti");
      }
      await loadData();
    } catch (err) {
      alert("Errore salvataggio Cryo a cuore: " + err.message);
    } finally {
      setAssigningDevice(null);
    }
  };

  if (loading) {
    return <div>Caricamento dispositivi...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-gradient-to-r from-slate-900 via-indigo-700 to-sky-500 p-4 text-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15">
              <Cpu size={22} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide opacity-80">
                Inventario dispositivi
              </div>
              <div className="text-xl font-semibold">
                {effectiveIsSuper
                  ? "Cryo, manuali e LoRaWAN"
                  : "Dispositivi visibili per la tua azienda"}
              </div>
              <div className="mt-1 text-xs opacity-90">
                Filtra per tipologia, azienda assegnata, stato frigo e codice MAC.
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <StatChip label="Totali" value={totals.total} icon={Cpu} />
            <StatChip
              label="Cryo reali"
              value={totals.cryoReal}
              icon={Thermometer}
              subtle
            />
            <StatChip
              label="A cuore"
              value={totals.cryoCore}
              icon={Thermometer}
              subtle
            />
            <StatChip label="LoRaWAN" value={totals.lorawan} icon={Radio} subtle />
            <StatChip
              label="Con azienda"
              value={totals.companyAssigned}
              icon={Building2}
              subtle
            />
          </div>
        </div>
      </div>

      {errore ? (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-100 px-3 py-2 text-sm text-amber-900">
          <AlertTriangle size={16} /> {errore}
        </div>
      ) : null}

      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Thermometer size={16} /> Filtri dispositivi
          </h2>
          <p className="text-xs text-slate-500">
            {effectiveIsSuper
              ? "Vista amministrativa completa di tutti i dispositivi."
              : `Vista limitata all'azienda ${aziendaId || "n/d"}.`}
          </p>
        </div>

        <div className="grid gap-3 lg:grid-cols-4">
          <SearchField
            value={macQuery}
            onChange={setMacQuery}
            placeholder="Filtra per MAC..."
          />

          <ToggleGroup
            value={typeFilter}
            onChange={setTypeFilter}
            options={[
              ["all", "Tutti"],
              ["cryo_core", "Abbattimenti"],
              ["cryo_real", "Cryo reali"],
              ["cryo_manual", "Manuali"],
              ["lorawan", "LoRaWAN"],
            ]}
          />

          <ToggleGroup
            value={companyFilter}
            onChange={setCompanyFilter}
            options={[
              ["all", "Tutti"],
              ["company_assigned", "Con azienda"],
              ["company_free", "Senza azienda"],
            ]}
          />

          <ToggleGroup
            value={frigoFilter}
            onChange={setFrigoFilter}
            options={[
              ["all", "Tutti"],
              ["assigned", "Su frigo"],
              ["free", "Liberi"],
            ]}
          />
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {filteredDispositivi.map((item) => {
          const kind = getDeviceKind(item);
          const assignedToFrigo = isAssignedToFrigo(item);
          const assignedToCompany = isAssignedToCompany(item);
          const mac = item.mac_address || item.mac || "MAC n/d";
          const nome = item.nome || item.label || item.nome_sonda || "-";
          const aziendaLabel =
            item.azienda_nome || item.aziendaName || item.azienda || item.azienda_id;
          const frigoLabel =
            item.frigo_nome || item.frigoName || item.frigo_mac || item.frigo_id;
          const temp =
            item.temp !== undefined && item.temp !== null ? `${item.temp} C` : null;
          const lastSeen = item.last_seen || item.lastSeen || item.ultima_lettura || null;

          return (
            <div
              key={`${kind}-${mac}`}
              className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">
                      {getKindLabel(kind)}
                    </div>
                    <Badge tone={kindTone(kind)}>{getKindLabel(kind)}</Badge>
                  </div>
                  <div className="mt-1 text-sm font-semibold">{nome}</div>
                  <div className="mt-1 font-mono text-[11px] text-slate-500">
                    MAC: {mac}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1">
                  <Badge tone={assignedToFrigo ? "emerald" : "amber"}>
                    {assignedToFrigo ? "Su frigo" : "Libero"}
                  </Badge>
                  <Badge tone={assignedToCompany ? "sky" : "slate"}>
                    {assignedToCompany ? "Con azienda" : "Senza azienda"}
                  </Badge>
                  {kind !== "lorawan" ? (
                    <button
                      onClick={() => rinominaSonda(item)}
                      className="mt-1 inline-flex items-center gap-1 rounded-full bg-slate-900 px-2 py-1 text-[11px] text-white hover:bg-slate-800"
                    >
                      <Edit size={11} /> Rinomina
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-col gap-1 text-xs text-slate-600">
                <div className="flex items-center gap-1">
                  <Building2 size={12} className="text-slate-400" />
                  <span className="font-medium">Azienda:</span>
                  <span>{aziendaLabel || "n/d"}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Refrigerator size={12} className="text-slate-400" />
                  <span className="font-medium">Frigo:</span>
                  <span>{frigoLabel || (assignedToFrigo ? "-" : "Non assegnato")}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Thermometer size={12} className="text-slate-400" />
                  <span className="font-medium">Temperatura:</span>
                  <span>{temp || "n/d"}</span>
                </div>
                {lastSeen ? (
                  <div className="flex items-center gap-1">
                    <Activity size={12} className="text-slate-400" />
                    <span className="font-medium">Ultimo contatto:</span>
                    <span>{lastSeen}</span>
                  </div>
                ) : null}
              </div>

              {kind !== "lorawan" ? (
                <div className="mt-2 rounded-xl border border-cyan-100 bg-cyan-50 p-2">
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 text-xs font-semibold text-cyan-900">
                      <input
                        type="checkbox"
                        checked={!!coreFlagByDevice[item.device_id || item.id]}
                        onChange={(e) =>
                          setCoreFlagByDevice((prev) => ({
                            ...prev,
                            [item.device_id || item.id]: e.target.checked,
                          }))
                        }
                      />
                      E dispositivo a cuore?
                    </label>
                    {!!coreFlagByDevice[item.device_id || item.id] && (
                      <input
                        type="number"
                        min="1"
                        value={assignCompanyByDevice[item.device_id || item.id] || ""}
                        onChange={(e) =>
                          setAssignCompanyByDevice((prev) => ({
                            ...prev,
                            [item.device_id || item.id]: e.target.value,
                          }))
                        }
                        placeholder="ID azienda"
                        className="rounded-lg border border-cyan-200 bg-white px-2 py-1 text-xs text-slate-700"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => salvaCryoAbbattimenti(item)}
                      disabled={assigningDevice === (item.device_id || item.id)}
                      className="rounded-lg bg-cyan-700 px-2 py-1.5 text-xs font-semibold text-white hover:bg-cyan-800 disabled:opacity-50"
                    >
                      {assigningDevice === (item.device_id || item.id)
                        ? "Salvo..."
                        : "Salva"}
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="mt-1 text-[11px] text-slate-400">
                Per assegnare un dispositivo a un frigo usa la sezione{" "}
                <span className="font-semibold">"Aziende &amp; Frighi"</span>.
              </div>
            </div>
          );
        })}

        {!filteredDispositivi.length ? (
          <div className="text-sm text-slate-500">
            Nessun dispositivo trovato con i filtri correnti.
          </div>
        ) : null}
      </div>
    </div>
  );
}
